/**
 * QC Status Board Component
 * Shows quality control inspection status
 */
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, CheckCircle, XCircle, Clock, Loader, ArrowRight } from 'lucide-react';

const QCStatusBoard = ({ data, isFullScreen }) => {
  const navigate = useNavigate();

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <ClipboardCheck className="w-6 h-6 mr-2 text-blue-600" />
          QC Status Board
        </h2>
        <div className="text-center py-8 text-gray-500">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Loading QC status...</p>
        </div>
      </div>
    );
  }

  const stats = data.stats || { total: 0, passed: 0, failed: 0, pending: 0, pass_rate: 0 };
  const recent = data.recent_inspections || [];

  const getStatusColor = (status) => {
    const colors = {
      PASSED: 'bg-green-100 text-green-800',
      FAILED: 'bg-red-100 text-red-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      IN_PROGRESS: 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      PASSED: CheckCircle,
      FAILED: XCircle,
      PENDING: Clock,
      IN_PROGRESS: Loader,
    };
    return icons[status] || Clock;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className={`font-bold text-gray-800 flex items-center ${isFullScreen ? 'text-lg' : 'text-xl'}`}>
          <ClipboardCheck className="w-6 h-6 mr-2 text-blue-600" />
          QC Status Board
        </h2>
        <div className="text-right">
          <div className="text-3xl font-bold text-green-600">
            {Math.round(stats.pass_rate || 0)}%
          </div>
          <div className="text-sm text-gray-600">Pass Rate</div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-gray-800">{stats.total || 0}</div>
          <div className="text-xs text-gray-600">Total</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.passed || 0}</div>
          <div className="text-xs text-gray-600">Passed</div>
        </div>
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-red-600">{stats.failed || 0}</div>
          <div className="text-xs text-gray-600">Failed</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</div>
          <div className="text-xs text-gray-600">Pending</div>
        </div>
      </div>

      {/* Quick Action Button */}
      {stats.pending > 0 && (
        <div className="mb-4">
          <button
            onClick={() => navigate('/qc-inspection')}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 shadow-md hover:shadow-lg"
          >
            <ClipboardCheck className="w-5 h-5" />
            <span>Start QC Inspection ({stats.pending} Pending)</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Recent Inspections */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Inspections</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {recent.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">
              No recent inspections
            </div>
          ) : (
            recent.map((inspection, index) => {
              const StatusIcon = getStatusIcon(inspection.status);

              return (
                <div
                  key={inspection.id || index}
                  className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-semibold text-gray-800 text-sm">
                          {inspection.material_name || inspection.material_code || 'N/A'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(inspection.status)}`}>
                          {inspection.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 space-y-0.5">
                        <div>Truck: {inspection.truck_number || 'N/A'}</div>
                        <div>PO: {inspection.po_number || 'N/A'}</div>
                        {inspection.inspector && <div>Inspector: {inspection.inspector}</div>}
                        {inspection.defect_rate !== null && inspection.defect_rate !== undefined && (
                          <div className={inspection.defect_rate > 5 ? 'text-red-600 font-semibold' : ''}>
                            Defect Rate: {inspection.defect_rate.toFixed(2)}%
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4">
                      <StatusIcon className={`w-8 h-8 ${
                        inspection.status === 'PASSED' ? 'text-green-500' :
                        inspection.status === 'FAILED' ? 'text-red-500' :
                        inspection.status === 'PENDING' ? 'text-yellow-500' :
                        'text-blue-500'
                      }`} />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Summary Bar */}
      {stats.total > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>Today's Performance</span>
            <span>{stats.passed}/{stats.total} Passed</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden flex">
            <div
              className="bg-green-500 h-3 transition-all duration-500"
              style={{ width: `${(stats.passed / stats.total) * 100}%` }}
              title={`Passed: ${stats.passed}`}
            />
            <div
              className="bg-red-500 h-3 transition-all duration-500"
              style={{ width: `${(stats.failed / stats.total) * 100}%` }}
              title={`Failed: ${stats.failed}`}
            />
            <div
              className="bg-yellow-500 h-3 transition-all duration-500"
              style={{ width: `${(stats.pending / stats.total) * 100}%` }}
              title={`Pending: ${stats.pending}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default QCStatusBoard;
